/** Pre-formatted, locale-aware string per cell. Calculated by row-vm.builder. */
export interface ValueCell {
    /** Formatted CHF value, or `-` when missing. */
    display: string;
    /** Verhältniskennzahl percentage cell, or null when struct mode is off. */
    structPct: string | null;
    /** Position in the rendered year list (0 = newest). Used to drive alternating
     *  blue/white column backgrounds — even indices (0, 2, 4) get the blue band. */
    columnIndex: number;
}

export type DeltaClassification = 'good' | 'bad' | 'neutral' | 'flat' | 'na';

export interface DeltaCell {
    /** Pre-formatted: `12.5 %` / `CHF 1’234’567.00` / `-` (n/a placeholder). */
    display: string;
    classification: DeltaClassification;
}

/**
 * Row representing any node in the harmonized tree.
 * `level === 0` → renders with `.section-header-row` (assets / liabilities / income-statement root).
 * `level >= 1` and `nodeType==='GROUP'` → `.group-row`.
 * `nodeType==='FLI'|'SUB_FLI'|'OF_THAT_ITEM'` → `.fli-row`.
 * `nodeType==='SUB_TOTAL'` → `.subtotal-row`.
 */
export interface TreeRowVM {
    kind: 'tree';
    nodeId: string;
    level: number;
    label: string;
    nodeType: 'GROUP' | 'FLI' | 'SUB_FLI' | 'OF_THAT_ITEM' | 'SUB_TOTAL';
    /** Has children worth rendering when expanded. */
    isExpandable: boolean;
    isExpanded: boolean;
    values: ValueCell[];
    delta: DeltaCell;
    /** True for the Verhältniskennzahl reference FLI (e.g. Betriebsertrag). */
    isReference: boolean;
    /** True when this is the final ER summary row that we render at the bottom of
     *  the income statement (the section root, repositioned to last). Drives the
     *  prominent `.er-total-row` styling. */
    isErTotal?: boolean;
}

/** Account leaf row. */
export interface AccountRowVM {
    kind: 'account';
    nodeId: string;
    level: number;
    /** Stable account number (badge). */
    accountNumber: string;
    label: string;
    values: ValueCell[];
    delta: DeltaCell;
}

/** 16 px spacer row between top-level sections (see `.section-gap` in SCSS). */
export interface GapRowVM {
    kind: 'gap';
}

export type RowVM = TreeRowVM | AccountRowVM | GapRowVM;
